// src/components/ErrorBoundary.tsx
import React from 'react';
import { Typography, Box, Button, Stack } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import { trackError } from '@/utils/analytics';

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(_: Error): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
        // エラーをトラッキング
        trackError(error, errorInfo, true);
    }

    private handleReload = () => {
        window.location.reload();
    };

    private handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            return (
                <Box
                    sx={{
                        minHeight: '60vh',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 3,
                        textAlign: 'center',
                        gap: 2,
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        予期せぬエラーが発生しました
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
                        ご不便をおかけします。ページを再読み込みするか、ホームに戻ってお試しください。
                        問題が続く場合は、しばらく時間をおいてから再度アクセスしてください。
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                        <Button
                            variant="contained"
                            startIcon={<RefreshIcon />}
                            onClick={this.handleReload}
                        >
                            再読み込み
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<HomeIcon />}
                            onClick={this.handleGoHome}
                        >
                            ホームに戻る
                        </Button>
                    </Stack>
                </Box>
            );
        }
        return this.props.children;
    }
}

export default ErrorBoundary;
